/*
 * <custom-cart-drawer>
 * Luxury cart drawer. Opens on cart-icon click and after add-to-cart.
 * Remove / quantity use the AJAX Cart API; the drawer re-renders via the
 * Section Rendering API (perfect server-side money + Liquid). No libraries.
 */
(function () {
  'use strict';

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  class CustomCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = 'custom-cart-drawer';
      this.panel = this.querySelector('.ccd__panel');
      this.inner = this.querySelector('[data-ccd-inner]');
      this.lastFocus = null;
      this._internal = false;

      // Stop the theme's own cart drawer from auto-opening (avoid two drawers).
      const themeDrawer = document.querySelector('cart-drawer-component, cart-drawer');
      if (themeDrawer) themeDrawer.removeAttribute('auto-open');

      this.onClick = this.onClick.bind(this);
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onAddSubmit = this.onAddSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTrap = this.onTrap.bind(this);

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onTriggerClick, true); // capture: beat theme handlers
      document.addEventListener('submit', this.onAddSubmit, true); // optimistic open on add
      document.addEventListener('keydown', this.onKeydown);
      ['cart:update', 'cart:refresh', 'product:added-to-cart', 'cart-update'].forEach((n) =>
        document.addEventListener(n, this.onCartUpdate)
      );
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick, true);
      document.removeEventListener('submit', this.onAddSubmit, true);
      document.removeEventListener('keydown', this.onKeydown);
      ['cart:update', 'cart:refresh', 'product:added-to-cart', 'cart-update'].forEach((n) =>
        document.removeEventListener(n, this.onCartUpdate)
      );
    }

    /* Open instantly the moment an add-to-cart form is submitted, so the user
       never waits on the network. The item populates when the add completes. */
    onAddSubmit(e) {
      const form = e.target;
      if (!form || typeof form.matches !== 'function') return;
      const action = form.getAttribute('action') || '';
      if (/\/cart\/add($|\?)/.test(action) || form.matches('[data-csp-form], product-form form, form[is="product-form"]')) {
        this.classList.add('ccd--loading');
        this.open();
        this.renderPlaceholder(form);
        // Reconcile with the server even if the add ultimately fails (no event fires).
        clearTimeout(this._reconcile);
        this._reconcile = setTimeout(() => this.refresh(), 8000);
      }
    }

    // Instantly show a temporary item row built from the product page, so the
    // shopper sees their item immediately instead of waiting on the network.
    renderPlaceholder(form) {
      const scope = form.closest('custom-stacked-product, .shopify-section') || document;
      const imgEl = scope.querySelector('.csp__media-img, .product__media img, img[src*="/cdn/shop/"]')
        || document.querySelector('.csp__media-img');
      const titleEl = scope.querySelector('.csp__title') || scope.querySelector('h1') || document.querySelector('.csp__title');
      const priceEl = scope.querySelector('[data-csp-price]') || document.querySelector('[data-csp-price]');
      if (!titleEl) return;

      const img = imgEl ? (imgEl.currentSrc || imgEl.src) : '';
      const title = (titleEl.textContent || '').trim();
      const price = priceEl ? (priceEl.textContent || '').trim() : '';

      const li = document.createElement('li');
      li.className = 'ccd__item ccd__item--placeholder';
      li.innerHTML =
        '<span class="ccd__item-media">' + (img ? '<img src="' + img + '" alt="">' : '') + '</span>' +
        '<div class="ccd__item-info">' +
        '<h2 class="ccd__item-title">' + title + '</h2>' +
        (price ? '<div class="ccd__item-price">' + price + '</div>' : '') +
        '</div>';

      let list = this.querySelector('.ccd__items');
      if (!list) {
        const panel = this.querySelector('[data-ccd-panel="bag"]');
        if (panel) {
          panel.outerHTML = '<div class="ccd__body" data-ccd-panel="bag"><ul class="ccd__items" role="list"></ul></div>';
          list = this.querySelector('.ccd__items');
        }
      }
      if (list) list.insertBefore(li, list.firstChild);

      // Optimistically bump the "Bag (n)" counter.
      const bagTab = this.querySelector('[data-ccd-tab="bag"]');
      if (bagTab) {
        const m = bagTab.textContent.match(/\d+/);
        bagTab.textContent = 'Bag (' + ((m ? parseInt(m[0], 10) : 0) + 1) + ')';
      }
    }

    /* --------------------------------------------------------- Open triggers */
    onTriggerClick(e) {
      if (this.contains(e.target)) return; // ignore clicks inside the drawer (e.g. View Bag)
      const trigger = e.target.closest('cart-icon, [data-ccd-open], a[href$="/cart"]');
      if (!trigger) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return; // let new-tab clicks through
      e.preventDefault();
      e.stopImmediatePropagation();
      this.lastFocus = trigger;
      this.open();
    }

    onCartUpdate() {
      // Open instantly so it feels responsive, then refresh contents in the
      // background (don't make the user wait on the section re-render).
      clearTimeout(this._reconcile);
      if (!this._internal) this.open();
      this.refresh();
    }

    /* --------------------------------------------------------- In-drawer clicks */
    onClick(e) {
      const t = e.target;
      if (t.closest('[data-ccd-close]') || t.closest('[data-ccd-overlay]')) {
        this.close();
        return;
      }
      const tab = t.closest('[data-ccd-tab]');
      if (tab) {
        this.setTab(tab.getAttribute('data-ccd-tab'));
        return;
      }
      const remove = t.closest('[data-ccd-remove]');
      if (remove) {
        e.preventDefault();
        this.changeLine(remove.getAttribute('data-line'), 0);
        return;
      }
      const qty = t.closest('[data-ccd-qty]');
      if (qty) {
        const line = qty.getAttribute('data-line');
        const dir = qty.getAttribute('data-ccd-qty');
        const valueEl = qty.parentElement.querySelector('[data-ccd-qty-value]');
        const cur = parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
        this.changeLine(line, dir === 'plus' ? cur + 1 : Math.max(0, cur - 1));
        return;
      }
      const save = t.closest('[data-ccd-save]');
      if (save) {
        this.saveForLater(save);
        return;
      }
    }

    setTab(tab) {
      this.setAttribute('data-tab', tab);
      this.querySelectorAll('[data-ccd-tab]').forEach((b) =>
        b.setAttribute('aria-selected', b.getAttribute('data-ccd-tab') === tab ? 'true' : 'false')
      );
    }

    /* ----------------------------------------------------------- Open / close */
    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this.addEventListener('keydown', this.onTrap);
      if (this.panel) window.requestAnimationFrame(() => this.panel.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      this.removeEventListener('keydown', this.onTrap);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
    }

    /* ----------------------------------------------------------- Cart actions */
    changeLine(line, quantity) {
      this._internal = true;
      return fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      })
        .then((r) => r.json())
        .then((cart) => {
          this.updateHeaderBubble(cart.item_count);
          return this.refresh();
        })
        .then(() => {
          // Let the host theme update its own header bubble too.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(() => {})
        .finally(() => {
          this._internal = false;
        });
    }

    saveForLater(btn) {
      // Hand off to any wishlist app, then remove the line from the bag.
      this.dispatchEvent(
        new CustomEvent('wishlist:save', {
          bubbles: true,
          detail: {
            productId: btn.getAttribute('data-product-id'),
            variantId: btn.getAttribute('data-variant-id'),
            productUrl: btn.getAttribute('data-product-url'),
          },
        })
      );
      this.changeLine(btn.getAttribute('data-line'), 0);
    }

    /* ------------------------------------------------- Section Rendering refresh */
    refresh() {
      const current = this.getAttribute('data-tab') || 'bag';
      return fetch(`${window.location.pathname}?sections=${this.sectionId}`)
        .then((r) => r.json())
        .then((data) => {
          const html = data[this.sectionId];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('[data-ccd-inner]');
          if (fresh && this.inner) this.inner.innerHTML = fresh.innerHTML;
          this.setTab(current); // preserve the active tab across refresh
        })
        .catch(() => {})
        .finally(() => this.classList.remove('ccd--loading'));
    }

    updateHeaderBubble(count) {
      if (typeof count !== 'number') return;
      document
        .querySelectorAll('.cart-bubble__text-count, .cart-count-bubble span[aria-hidden="true"], [data-ccd-cart-count]')
        .forEach((el) => {
          el.textContent = count;
        });
    }

    /* ----------------------------------------------------------- Keyboard / a11y */
    onKeydown(e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && this.classList.contains('is-open')) {
        this.close();
      }
    }

    onTrap(e) {
      if (e.key !== 'Tab' || !this.panel) return;
      const focusable = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('custom-cart-drawer')) {
    customElements.define('custom-cart-drawer', CustomCartDrawer);
  }
})();
